import {
  Component,
  InputSignal,
  OnInit,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  inject,
} from "@angular/core";

import { Product } from "@core/api-models/products.models";
import { ProductsService } from "@core/api/products.service";
import { ProductFilter } from "@core/models/products.types";

import { TableCallback } from "@theme/@confect/components/confect-table/confect-table.component";

import { Subject, debounceTime, map, takeUntil } from "rxjs";

@Component({
  selector: "ngx-products-search-v2",
  standalone: false,
  templateUrl: "./products-search-v2.component.html",
  styleUrls: ["./products-search-v2.component.scss"],
})
export class ProductsSearchV2Component implements OnInit {
  private ps = inject(ProductsService);

  unsubscribe$ = new Subject();

  selectedProductsInput: InputSignal<string[]> = input([]);
  selectedProductsChange: OutputEmitterRef<Product[] | string[]> = output();

  productFilters: InputSignal<ProductFilter> = input({});
  _productFilters: Signal<ProductFilter> = computed(() => {
    const filter = JSON.parse(JSON.stringify(this.productFilters()));
    delete filter.product_id;
    delete filter.ext_ids;
    return filter;
  });
  noBorders: InputSignal<boolean> = input(false);
  emitProductObjects: InputSignal<boolean> = input(false);

  // Pagenation
  shouldLoadNextPage: WritableSignal<boolean> = signal(true);
  isLoading: WritableSignal<boolean> = signal(false);
  per_page: WritableSignal<number> = signal(30);
  page: WritableSignal<number> = signal(1);

  // Search
  searchResultProducts: WritableSignal<Product[]> = signal([]);
  searchString: WritableSignal<string> = signal("");

  //Selected
  selectedProducts: WritableSignal<Product[]> = signal([]);
  selectedSet: Signal<Set<string>> = computed(() => {
    return new Set(this.selectedProducts().map((product) => product.ext_id));
  });
  checkSelected: Signal<Record<string, boolean>> = computed(() => {
    const check = {};
    this.searchResultProducts().forEach((product) => {
      check[product.ext_id] = this.selectedSet().has(product.ext_id);
    });
    return check;
  });
  searchChange = new Subject<boolean>();

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    effect(() => {
      if (
        Object.values(this.productFilters()).length === 0 ||
        this.selectedProductsInput().length === 0
      ) {
        return;
      }

      const usesProductID = this.productFilters().product_id != null;
      const inputProductIDs = this.selectedProductsInput();
      untracked(() => {
        this.ps
          .searchProducts({
            page_size: inputProductIDs.length,
            product_filter: {
              ...(usesProductID
                ? { product_id: inputProductIDs }
                : { ext_ids: inputProductIDs }),
            },
          })
          .subscribe({
            next: (res) => {
              if (usesProductID) {
                this.productFilters().ext_ids =
                  this.productFilters().product_id.map((product_id) => {
                    return res.data.find(
                      (product) =>
                        product.id.toString() === product_id.toString(),
                    )?.ext_id;
                  });
              }
              this.selectedProducts.set(
                res.data.map((p) => {
                  p["cdn_image"] = p["cdn_image"] + "?width=100";
                  return p;
                }),
              );
            },
          });
      });
    });
  }

  ngOnInit(): void {
    this.search();

    this.searchChange
      .pipe(
        debounceTime(300),
        map((_) => this.search()),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();
  }

  addProduct(product) {
    this.selectedProducts.update((products) => {
      const newProducts = JSON.parse(JSON.stringify(products));
      newProducts.push(product);
      return newProducts;
    });
  }

  removeProduct(product) {
    this.selectedProducts.update((products) => {
      const newProducts = JSON.parse(JSON.stringify(products));
      const index = newProducts.findIndex((p) => p.ext_id === product.ext_id);
      newProducts.splice(index, 1);
      return newProducts;
    });
  }

  apply() {
    if (this.emitProductObjects()) {
      this.selectedProductsChange.emit(this.selectedProducts());
      return;
    }
    this.selectedProductsChange.emit(Array.from(this.selectedSet()));
  }

  search() {
    // Reset
    this.searchResultProducts.set([]);
    this.page.set(1);
    this.shouldLoadNextPage.set(true);

    // New search
    this.loadNextPage();
  }

  loadNextPage(callback: TableCallback = null) {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    this.ps
      .searchProducts({
        text: this.searchString(),
        page: this.page(),
        page_size: this.per_page(),
        product_filter: this._productFilters(),
      })
      .subscribe((res) => {
        this.searchResultProducts.update((result) => {
          return result.concat(
            res.data.map((p) => {
              p["cdn_image"] = p["cdn_image"] + "?width=100";
              return p;
            }),
          );
        });

        if (callback != null) {
          callback(res.data.length === this.per_page());
        }
        this.page.update((page) => page + 1);

        this.isLoading.set(false);
      });
  }
}
